import { createApiRef } from "@backstage/core";
import { DiscoveryApi, IdentityApi } from "@backstage/core-plugin-api";
import { DatadogCiRetryResult } from "../types";
import axios from "axios";

export const datadogApiRef = createApiRef<DatadogApi>({
  id: "plugin.datadog-api.service",
  description: "Used by plugins to make requests to Datadog",
});

type Options = {
  discoveryApi: DiscoveryApi;
  identityApi: IdentityApi;
};

export class DatadogApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly identityApi: IdentityApi;

  constructor(options: Options) {
    this.discoveryApi = options.discoveryApi;
    this.identityApi = options.identityApi;
  }

  /** Retrieve the primary URL  */
  private async getUrl(): Promise<string> {
    return await this.discoveryApi.getBaseUrl("datadog-api");
  }

  private async headers(): Promise<{ Authorization: string }> {
    const { token } = await this.identityApi.getCredentials();
    return { Authorization: `Bearer ${token}` };
  }

  public async getSlo(id: string, days: number = 7) {
    const resp = await axios.get(
      `${await this.getUrl()}/slo?id=${id}&days=${days}`,
      { headers: await this.headers() }
    );

    return resp.data;
  }

  public async getSlosByTeam(team: string) {
    const { data } = await axios.get(
      `${await this.getUrl()}/getSlosByTeam?team=${team}`,
      { headers: await this.headers() }
    );

    return data;
  }

  public async getCiRetries({
    limit = 10,
  }: {
    limit?: number;
  }): Promise<{ data: DatadogCiRetryResult[] }> {
    const { data } = await axios.get(
      `${await this.getUrl()}/getCiRetries?limit=${limit}`,
      { headers: await this.headers() }
    );

    return data;
  }

  public async getIncidents({
    team,
    resolved = false,
    fromDaysInPast = 7,
  }: {
    team?: string;
    resolved?: boolean;
    fromDaysInPast?: number;
  }) {
    let query = `fromDaysInPast=${fromDaysInPast}`;

    if (resolved !== undefined) {
      query += `&resolved=${resolved}`;
    }

    if (team) {
      query += `&team=${team}`;
    }

    const { data } = await axios.get(
      `${await this.getUrl()}/getIncidents?${query}`,
      { headers: await this.headers() }
    );

    return data;
  }
}
